/**
 * Utilities for persisting unread conversation counts and message IDs in localStorage
 */
const UNREAD_COUNTS_KEY = 'inbox_unread_counts';
const UNREAD_MESSAGE_IDS_KEY = 'inbox_unread_message_ids';
const TOTAL_UNREAD_COUNT_KEY = 'inbox_total_unread_count';
/**
 * Load unread counts from localStorage
 */
export const loadUnreadCounts = () => {
    try {
        const stored = localStorage.getItem(UNREAD_COUNTS_KEY);
        return stored ? JSON.parse(stored) : {};
    }
    catch (error) {
        console.warn('Failed to load unread counts from localStorage:', error);
        return {};
    }
};
/**
 * Save unread counts to localStorage
 */
export const saveUnreadCounts = (unreadCounts) => {
    try {
        localStorage.setItem(UNREAD_COUNTS_KEY, JSON.stringify(unreadCounts));
    }
    catch (error) {
        console.warn('Failed to save unread counts to localStorage:', error);
    }
};
/**
 * Load unread message IDs from localStorage
 */
export const loadUnreadMessageIds = () => {
    try {
        const stored = localStorage.getItem(UNREAD_MESSAGE_IDS_KEY);
        return stored ? JSON.parse(stored) : {};
    }
    catch (error) {
        console.warn('Failed to load unread message IDs from localStorage:', error);
        return {};
    }
};
/**
 * Save unread message IDs to localStorage
 */
export const saveUnreadMessageIds = (unreadMessageIds) => {
    try {
        localStorage.setItem(UNREAD_MESSAGE_IDS_KEY, JSON.stringify(unreadMessageIds));
    }
    catch (error) {
        console.warn('Failed to save unread message IDs to localStorage:', error);
    }
};
/**
 * Load total unread count from localStorage
 */
export const loadTotalUnreadCount = () => {
    try {
        const stored = localStorage.getItem(TOTAL_UNREAD_COUNT_KEY);
        return stored ? parseInt(stored, 10) : 0;
    }
    catch (error) {
        console.warn('Failed to load total unread count from localStorage:', error);
        return 0;
    }
};
/**
 * Save total unread count to localStorage
 */
export const saveTotalUnreadCount = (count) => {
    try {
        localStorage.setItem(TOTAL_UNREAD_COUNT_KEY, count.toString());
    }
    catch (error) {
        console.warn('Failed to save total unread count to localStorage:', error);
    }
};
/**
 * Clear all unread counts and message IDs from localStorage
 */
export const clearUnreadCounts = () => {
    try {
        localStorage.removeItem(UNREAD_COUNTS_KEY);
        localStorage.removeItem(UNREAD_MESSAGE_IDS_KEY);
        localStorage.removeItem(TOTAL_UNREAD_COUNT_KEY);
    }
    catch (error) {
        console.warn('Failed to clear unread counts from localStorage:', error);
    }
};
/**
 * Update unread count for a specific conversation and return updated state
 */
export const updateUnreadCountForConversation = (conversationId, increment) => {
    const currentCounts = loadUnreadCounts();
    const currentTotal = loadTotalUnreadCount();
    let newTotal = currentTotal;
    const newCounts = Object.assign({}, currentCounts);
    if (increment) {
        // Increment count for this conversation
        newCounts[conversationId] = (newCounts[conversationId] || 0) + 1;
        newTotal = currentTotal + 1;
    }
    else {
        // Clear count for this conversation
        const conversationCount = newCounts[conversationId] || 0;
        if (conversationCount > 0) {
            newTotal = Math.max(0, currentTotal - conversationCount);
            delete newCounts[conversationId];
        }
    }
    // Save to localStorage
    saveUnreadCounts(newCounts);
    saveTotalUnreadCount(newTotal);
    return {
        totalCount: newTotal,
        unreadCounts: newCounts,
    };
};
/**
 * Add message ID to unread messages for a conversation
 */
export const addUnreadMessageId = (conversationId, messageId) => {
    const currentUnreadCounts = loadUnreadCounts();
    const currentUnreadMessageIds = loadUnreadMessageIds();
    // Add message ID to the conversation's unread messages
    if (!currentUnreadMessageIds[conversationId]) {
        currentUnreadMessageIds[conversationId] = [];
    }
    if (!currentUnreadMessageIds[conversationId].includes(messageId)) {
        currentUnreadMessageIds[conversationId].push(messageId);
        currentUnreadCounts[conversationId] = (currentUnreadCounts[conversationId] || 0) + 1;
    }
    // Calculate total
    const totalCount = Object.values(currentUnreadCounts).reduce((sum, count) => sum + count, 0);
    // Save to localStorage
    saveUnreadCounts(currentUnreadCounts);
    saveUnreadMessageIds(currentUnreadMessageIds);
    saveTotalUnreadCount(totalCount);
    return {
        totalCount,
        unreadCounts: currentUnreadCounts,
        unreadMessageIds: currentUnreadMessageIds,
    };
};
/**
 * Remove message ID from unread messages for a conversation
 */
export const removeUnreadMessageId = (conversationId, messageId) => {
    const currentUnreadCounts = loadUnreadCounts();
    const currentUnreadMessageIds = loadUnreadMessageIds();
    // Remove message ID from the conversation's unread messages
    if (currentUnreadMessageIds[conversationId]) {
        const index = currentUnreadMessageIds[conversationId].indexOf(messageId);
        if (index > -1) {
            currentUnreadMessageIds[conversationId].splice(index, 1);
            currentUnreadCounts[conversationId] = Math.max(0, (currentUnreadCounts[conversationId] || 0) - 1);
            // Remove conversation if no unread messages
            if (currentUnreadMessageIds[conversationId].length === 0) {
                delete currentUnreadMessageIds[conversationId];
                delete currentUnreadCounts[conversationId];
            }
        }
    }
    // Calculate total
    const totalCount = Object.values(currentUnreadCounts).reduce((sum, count) => sum + count, 0);
    // Save to localStorage
    saveUnreadCounts(currentUnreadCounts);
    saveUnreadMessageIds(currentUnreadMessageIds);
    saveTotalUnreadCount(totalCount);
    return {
        totalCount,
        unreadCounts: currentUnreadCounts,
        unreadMessageIds: currentUnreadMessageIds,
    };
};
